/**
 * Usuários — quem opera o sistema, e o que cada um alcança.
 *
 * **Agora cadastra pessoas.** Até esta volta o operador só nascia no PainelAdm;
 * aqui se criava nada e só se marcava o que cada um alcança. É o que o
 * "Usuários" do menu antigo fazia — e lá o item está **comentado** desde algum
 * ponto, então esta tela ressuscita uma função que estava fora do ar.
 *
 * **A senha entra, mas nunca sai.** Nenhuma rota deste módulo devolve senha. Ela
 * ainda é texto plano no banco, e isso é deliberado enquanto o PainelAdm estiver
 * no ar: o login dele compara a senha direto no SQL, e gravar hash aqui trancaria
 * a pessoa para fora. A troca por hash depende de DDL na tabela `painel` de
 * produção — a coluna é `varchar(36)` e não cabe um hash.
 *
 * **"Ativo" aqui é o nível, e isso importa.** A tabela tem duas noções que
 * divergem: a coluna `ativo` vale 1 em **2** dos 128 operadores, mas
 * `nivel > 0` vale em **29** — e é o nível que o login exige, recusando nível
 * zero com "usuário desativado". Filtrar pela coluna `ativo` faria a tela dizer
 * que duas pessoas alcançam o sistema quando vinte e nove alcançam. A tela
 * mostra o nível, e marca quando a coluna discorda.
 *
 * Dado global — a trava recusa gravação em desenvolvimento.
 */

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"

const BASE = "api/controle-de-acesso"

type Operador = {
  id: number
  nome: string
  email: string
  nivel: number
  nivelNome: string
  /** Nível acima de zero — é o que o login exige. */
  podeEntrar: boolean
  /** A coluna `ativo` crua. Diverge do nível, e a divergência é do dado. */
  colunaAtivo: boolean
  quantidadeDeAcessos: number
}

type AcessoDoOperador = { acessarId: number; texto: string; grupo: string; tem: boolean }
type Chave = { id: number; texto: string; descricao: string; grupo: string }

type Visao = "operadores" | "chaves"

/**
 * Os números da barra de estado.
 *
 * Eram texto fixo: dizia "48 chaves" quando já são 53, e os outros dois
 * envelheceram junto. Número em tela ou é o de agora, ou não é número — então
 * sai da própria consulta, e some enquanto ela carrega em vez de mostrar um
 * valor de ontem.
 */
function Resumo() {
  const operadores = useQuery({
    queryKey: ["controle-de-acesso", "operadores"],
    queryFn: () => obter<Operador[]>(`${BASE}/operadores`, {}),
  })
  const chaves = useQuery({
    queryKey: ["controle-de-acesso", "chaves"],
    queryFn: () => obter<Chave[]>(`${BASE}/chaves`, {}),
  })

  if (!operadores.data || !chaves.data) return null
  const entram = operadores.data.filter((o) => o.podeEntrar).length
  return (
    <>
      {operadores.data.length} operadores · {entram} conseguem entrar ·{" "}
      {chaves.data.length} chaves
    </>
  )
}

export function Usuarios() {
  const [visao, setVisao] = useState<Visao>("operadores")
  const [recado, setRecado] = useState<string | null>(null)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Usuários">
        <div style={{ display: "flex", gap: 2, background: "var(--surface2)", padding: 2, borderRadius: 6 }}>
          {([["operadores", "Operadores"], ["chaves", "Chaves de permissão"]] as const).map(([c, r]) => (
            <button
              key={c}
              onClick={() => { setVisao(c); setRecado(null) }}
              style={{
                border: 0, borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                background: visao === c ? "var(--surface)" : "transparent",
                color: visao === c ? "var(--txt)" : "var(--txt2)",
                fontWeight: visao === c ? 600 : 400,
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Permissões valem para o sistema inteiro, não para um projeto" />

      {visao === "operadores" ? <Operadores aoAvisar={setRecado} /> : <Chaves aoAvisar={setRecado} />}

      {/* Era texto fixo — "48 chaves" quando já são 53. Número na tela ou é o de
          agora ou não é número: passa a sair da própria consulta. */}
      <BarraDeEstado direita={<Resumo />}>
        {recado ?? ""}
      </BarraDeEstado>
    </div>
  )
}

// ---------------------------------------------------------------- operadores

type Rascunho = {
  nome: string
  email: string
  nivel: number
  telefone: string
  ramal: string
  sexo: string
  senha: string
}

const RASCUNHO_VAZIO: Rascunho = {
  nome: "", email: "", nivel: 1, telefone: "", ramal: "", sexo: "", senha: "",
}

function Operadores({ aoAvisar }: { aoAvisar: (m: string | null) => void }) {
  // Abre em quem consegue entrar: 29 de 128.
  const [somenteAtivos, setSomenteAtivos] = useState(true)
  const [busca, setBusca] = useState("")
  const [aberto, setAberto] = useState<Operador | null>(null)
  const [cadastro, setCadastro] = useState<Rascunho | null>(null)
  const [editando, setEditando] = useState<Operador | null>(null)

  const lista = useQuery({
    queryKey: ["acesso-operadores", somenteAtivos] as const,
    queryFn: () => obter<Operador[]>(`${BASE}/operadores`,
      somenteAtivos ? { ativos: true } : {}),
  })

  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase()
    if (!t) return lista.data ?? []
    return (lista.data ?? []).filter((o) =>
      o.nome.toLowerCase().includes(t) || o.email.toLowerCase().includes(t))
  }, [lista.data, busca])

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <div style={{
        flex: "0 0 330px", width: 330, overflowY: "auto", padding: 12,
        borderRight: "1px solid var(--line2)",
      }}>
        <button
          onClick={() => { setCadastro(RASCUNHO_VAZIO); setAberto(null) }}
          style={{
            width: "100%", marginBottom: 8, padding: "6px 9px", borderRadius: 6,
            border: "1px solid var(--acao)", background: "var(--acao-soft)",
            color: "var(--acao-on-soft, var(--txt))", fontSize: 12, cursor: "pointer",
          }}
        >
          + Novo operador
        </button>

        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Filtrar por nome ou e-mail…"
          style={{ ...campo, marginBottom: 6 }}
        />
        <label style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 11.5,
          marginBottom: 10, cursor: "pointer",
        }}>
          <input type="checkbox" checked={somenteAtivos}
                 onChange={(e) => { setSomenteAtivos(e.target.checked); setAberto(null) }} />
          só quem consegue entrar
        </label>

        {lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
        ) : lista.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
            {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
          </p>
        ) : achados.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nenhum operador com esse texto.</p>
        ) : (
          achados.slice(0, 200).map((o) => (
            <button
              key={o.id}
              onClick={() => { setAberto(o); setCadastro(null) }}
              style={{
                display: "block", width: "100%", textAlign: "left", marginBottom: 4,
                border: "1px solid " + (aberto?.id === o.id ? "var(--acao)" : "var(--line2)"),
                borderRadius: 6, padding: "6px 9px", cursor: "pointer",
                background: aberto?.id === o.id ? "var(--acao-soft)" : "var(--surface)",
                opacity: o.podeEntrar ? 1 : 0.62,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{o.nome}</span>
                {!o.podeEntrar && (
                  <span style={{ fontSize: 9.5, color: "var(--txt3)" }}>não entra</span>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                {o.nivelNome} · {o.quantidadeDeAcessos} de 48
              </div>
              <div style={{ fontSize: 10, color: "var(--txt3)" }}>{o.email}</div>
            </button>
          ))
        )}

        {achados.length > 200 && (
          <p style={{ fontSize: 10.5, color: "var(--txt3)" }}>
            {achados.length} operadores; mostrando 200.
          </p>
        )}
      </div>

      {cadastro ? (
        <Cadastro
          inicial={cadastro}
          editando={editando}
          aoAvisar={aoAvisar}
          aoFechar={() => { setCadastro(null); setEditando(null) }}
        />
      ) : aberto ? (
        <Permissoes
          operador={aberto}
          aoAvisar={aoAvisar}
          aoEditar={() => {
            setEditando(aberto)
            setCadastro({
              nome: aberto.nome, email: aberto.email, nivel: aberto.nivel,
              telefone: "", ramal: "", sexo: "", senha: "",
            })
          }}
        />
      ) : (
        <div style={{ padding: 24, maxWidth: "62ch" }}>
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--txt3)", lineHeight: 1.6 }}>
            Escolha um operador para ver e mudar o que ele alcança. São 128 no total, e
            <b> 29 conseguem entrar</b> — por isso a lista abre filtrada.
          </p>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--txt3)", lineHeight: 1.6 }}>
            Para criar alguém, use <b>+ Novo operador</b>. Excluir não existe: quem sai da
            empresa vai a <b>nível 0</b>, e é assim que a autoria de pinouts e diagramas antigos
            continua fazendo sentido.
          </p>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--txt3)", lineHeight: 1.6 }}>
            <b>“Consegue entrar” é o nível, não a coluna <span className="mono">ativo</span>.</b> As
            duas divergem na base — a coluna vale 1 em 2 operadores, o nível em 29 — e é o nível
            que o login exige.
          </p>
        </div>
      )}
    </div>
  )
}

function Permissoes({ operador, aoAvisar, aoEditar }: {
  operador: Operador
  aoAvisar: (m: string | null) => void
  aoEditar: () => void
}) {
  const qc = useQueryClient()
  const acessos = useQuery({
    queryKey: ["acesso-do-operador", operador.id] as const,
    queryFn: () => obter<AcessoDoOperador[]>(`${BASE}/operadores/${operador.id}/acessos`, {}),
  })

  /** O que está marcado agora na tela — parte do que veio e muda com os cliques. */
  const [marcadas, setMarcadas] = useState<Set<number> | null>(null)
  const atual = marcadas ?? new Set((acessos.data ?? []).filter((a) => a.tem).map((a) => a.acessarId))

  const gravar = useMutation({
    mutationFn: async (ids: number[]) => {
      await api.put(`${BASE}/operadores/${operador.id}/acessos`,
        { acessarIds: ids }, foraDoProjeto())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["acesso-do-operador", operador.id] })
      qc.invalidateQueries({ queryKey: ["acesso-operadores"] })
      setMarcadas(null)
      aoAvisar(`Permissões de ${operador.nome} gravadas.`)
    },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  const porGrupo = useMemo(() => {
    const mapa = new Map<string, AcessoDoOperador[]>()
    for (const a of acessos.data ?? []) {
      const g = a.grupo?.trim() || "Sem grupo"
      mapa.set(g, [...(mapa.get(g) ?? []), a])
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0], "pt-BR"))
  }, [acessos.data])

  const originais = new Set((acessos.data ?? []).filter((a) => a.tem).map((a) => a.acessarId))
  const mudou = marcadas !== null &&
    (atual.size !== originais.size || [...atual].some((id) => !originais.has(id)))

  function alternar(id: number) {
    setMarcadas(() => {
      const n = new Set(atual)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <b style={{ fontSize: 13.5 }}>{operador.nome}</b>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>{operador.email}</span>
        <span style={{
          fontSize: 10, padding: "1px 6px", borderRadius: 3,
          background: operador.podeEntrar ? "var(--acao-soft)" : "var(--surface2)",
          color: operador.podeEntrar ? "var(--acao-on-soft)" : "var(--txt3)",
        }}>
          {operador.podeEntrar ? operador.nivelNome : "não entra (nível 0)"}
        </span>
        <button
          onClick={aoEditar}
          style={{
            marginLeft: "auto", padding: "3px 9px", borderRadius: 5, fontSize: 11,
            border: "1px solid var(--line2)", background: "var(--surface)",
            color: "var(--txt2)", cursor: "pointer",
          }}
        >
          Alterar dados
        </button>
      </div>

      {operador.podeEntrar && !operador.colunaAtivo && (
        <p style={{
          margin: "0 0 10px", padding: "6px 9px", borderRadius: 5, fontSize: 11,
          lineHeight: 1.5, background: "var(--aviso-soft, #fdf1d8)",
          color: "var(--aviso-on-soft, #7a4b00)", maxWidth: "76ch",
        }}>
          Este operador <b>entra no sistema</b> (nível {operador.nivel}), mas a coluna
          <span className="mono"> ativo</span> está zerada. São duas noções que a base guarda
          separadas e que divergem em 27 dos 29 que entram — quem manda é o nível.
        </p>
      )}
      <p style={{ margin: "0 0 14px", fontSize: 11.5, color: "var(--txt2)" }}>
        {atual.size} de {(acessos.data ?? []).length} permissões
        {mudou && <b style={{ color: "var(--acao)" }}> · alterado, ainda não gravado</b>}
      </p>

      {acessos.isPending ? (
        <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando as permissões…</p>
      ) : acessos.error ? (
        <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
          {acessos.error instanceof ErroDaApi ? acessos.error.message : "Não foi possível ler."}
        </p>
      ) : (
        <>
          <div style={{
            display: "grid", gap: 12,
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
          }}>
            {porGrupo.map(([grupo, chaves]) => {
              const todas = chaves.every((c) => atual.has(c.acessarId))
              return (
                <section key={grupo} style={{
                  border: "1px solid var(--line2)", borderRadius: 6, padding: "8px 10px",
                }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 5 }}>
                    <b style={{ fontSize: 11 }}>{grupo}</b>
                    <button
                      onClick={() => setMarcadas(() => {
                        const n = new Set(atual)
                        for (const c of chaves) todas ? n.delete(c.acessarId) : n.add(c.acessarId)
                        return n
                      })}
                      style={{
                        marginLeft: "auto", border: 0, background: "transparent",
                        color: "var(--acao)", cursor: "pointer", fontSize: 10.5,
                      }}
                    >
                      {todas ? "nenhuma" : "todas"}
                    </button>
                  </div>
                  {chaves.map((c) => (
                    <label key={c.acessarId} style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "2px 0",
                      fontSize: 11.5, cursor: "pointer",
                    }}>
                      <input type="checkbox" checked={atual.has(c.acessarId)}
                             onChange={() => alternar(c.acessarId)} />
                      <span className="mono" style={{ fontSize: 10.5 }}>{c.texto}</span>
                    </label>
                  ))}
                </section>
              )
            })}
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: 16, alignItems: "center" }}>
            <Botao
              destaque
              desligado={!mudou}
              ocupado={gravar.isPending}
              aoClicar={() => { aoAvisar(null); gravar.mutate([...atual]) }}
            >
              {gravar.isPending ? "Gravando…" : "Gravar permissões"}
            </Botao>
            {mudou && <Botao aoClicar={() => setMarcadas(null)}>Descartar</Botao>}
            <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
              Grava o conjunto inteiro — o que estiver marcado passa a ser o que ele tem.
            </span>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- chaves

function Chaves({ aoAvisar }: { aoAvisar: (m: string | null) => void }) {
  const qc = useQueryClient()
  const lista = useQuery({
    queryKey: ["acesso-chaves"] as const,
    queryFn: () => obter<Chave[]>(`${BASE}/chaves`, {}),
  })

  const [nova, setNova] = useState({ texto: "", descricao: "", grupo: "" })
  const [confirmando, setConfirmando] = useState<Chave | null>(null)
  const [editando, setEditando] = useState<Chave | null>(null)

  const limpar = () => {
    qc.invalidateQueries({ queryKey: ["acesso-chaves"] })
    qc.invalidateQueries({ queryKey: ["acesso-do-operador"] })
  }

  const criar = useMutation({
    mutationFn: async (dados: typeof nova) => {
      await api.post(`${BASE}/chaves`, dados, foraDoProjeto())
    },
    onSuccess: () => { limpar(); setNova({ texto: "", descricao: "", grupo: "" }); aoAvisar("Chave criada.") },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  /**
   * Alterar a chave.
   *
   * **O `texto` é o que o código pergunta.** Trocá-lo não renomeia a pergunta no
   * código: quem perguntava pelo texto antigo passa a não achar nada, e ninguém
   * mais passa por ali — sem erro em tela nenhuma. Por isso o formulário avisa, e
   * por isso quase sempre o que se quer mudar é a descrição ou o grupo.
   */
  const alterar = useMutation({
    mutationFn: async (c: Chave) => {
      await api.put(`${BASE}/chaves/${c.id}`,
        { texto: c.texto, descricao: c.descricao, grupo: c.grupo }, foraDoProjeto())
    },
    onSuccess: () => { limpar(); setEditando(null); aoAvisar("Chave alterada.") },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`${BASE}/chaves/${id}`, foraDoProjeto())
    },
    onSuccess: () => { limpar(); setConfirmando(null); aoAvisar("Chave e vínculos removidos.") },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  const grupos = useMemo(() => {
    const mapa = new Map<string, Chave[]>()
    for (const c of lista.data ?? []) {
      const g = c.grupo?.trim() || "Sem grupo"
      mapa.set(g, [...(mapa.get(g) ?? []), c])
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0], "pt-BR"))
  }, [lista.data])

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
      <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--txt2)", lineHeight: 1.6, maxWidth: "80ch" }}>
        As chaves que o código pergunta. O <b>texto</b> é o que aparece no código
        (<span className="mono">projeto_listar</span>, <span className="mono">conteudo</span>) e é único;
        o <b>grupo</b> só organiza a tela. São {(lista.data ?? []).length} em {grupos.length} grupos.
      </p>

      {lista.isPending ? (
        <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
      ) : lista.error ? (
        <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
          {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
        </p>
      ) : (
        <div style={{
          display: "grid", gap: 10,
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        }}>
          {grupos.map(([grupo, chaves]) => (
            <section key={grupo} style={{
              border: "1px solid var(--line2)", borderRadius: 6, padding: "8px 10px",
            }}>
              <b style={{ fontSize: 11 }}>{grupo}</b>
              {chaves.map((c) => (
                <div key={c.id} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "2px 0", fontSize: 11.5,
                }}>
                  <span className="mono" style={{ fontSize: 10.5, flex: 1 }} title={c.descricao}>
                    {c.texto}
                  </span>
                  <button
                    onClick={() => { setEditando(c); setConfirmando(null) }}
                    aria-label={`Alterar ${c.texto}`}
                    style={{
                      border: 0, background: "transparent", cursor: "pointer",
                      color: "var(--txt3)", fontSize: 10.5, lineHeight: 1,
                      textDecoration: "underline",
                    }}
                  >
                    alterar
                  </button>
                  <button
                    onClick={() => { setConfirmando(c); setEditando(null) }}
                    aria-label={`Excluir ${c.texto}`}
                    style={{
                      border: 0, background: "transparent", cursor: "pointer",
                      color: "var(--txt3)", fontSize: 13, lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}

      {editando && (
        <div style={{
          marginTop: 14, padding: 11, borderRadius: 6, maxWidth: "72ch",
          border: "1px solid var(--line)",
        }}>
          <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
            Alterando a chave <span className="mono"><b>{editando.texto}</b></span>.
            <b> Trocar o texto não renomeia a pergunta no código</b> — quem perguntava pelo texto
            antigo deixa de achar a chave e ninguém mais passa, sem erro em tela nenhuma. Descrição
            e grupo são seguros: só organizam esta tela.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <label style={{ fontSize: 10.5, color: "var(--txt2)" }}>
              Texto (o que o código pergunta)<br />
              <input value={editando.texto} className="mono"
                     onChange={(e) => setEditando({ ...editando, texto: e.target.value })}
                     style={{ ...campo, width: 220, marginTop: 3 }} />
            </label>
            <label style={{ fontSize: 10.5, color: "var(--txt2)" }}>
              Descrição<br />
              <input value={editando.descricao ?? ""}
                     onChange={(e) => setEditando({ ...editando, descricao: e.target.value })}
                     style={{ ...campo, width: 280, marginTop: 3 }} />
            </label>
            <label style={{ fontSize: 10.5, color: "var(--txt2)" }}>
              Grupo<br />
              <input value={editando.grupo ?? ""}
                     onChange={(e) => setEditando({ ...editando, grupo: e.target.value })}
                     style={{ ...campo, width: 180, marginTop: 3 }} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Botao destaque ocupado={alterar.isPending}
                   aoClicar={() => { aoAvisar(null); alterar.mutate(editando) }}>
              Gravar
            </Botao>
            <Botao aoClicar={() => setEditando(null)}>Cancelar</Botao>
          </div>
        </div>
      )}

      {confirmando && (
        <div style={{
          marginTop: 14, padding: 11, borderRadius: 6, maxWidth: "72ch",
          border: "1px solid var(--erro)",
        }}>
          <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
            Excluir a chave <span className="mono"><b>{confirmando.texto}</b></span> e
            <b> todas as permissões concedidas por ela</b>. Se o código ainda perguntar por essa
            chave, ninguém mais passa — e nenhuma tela vai dizer por quê.
          </p>
          <div style={{ display: "flex", gap: 6 }}>
            <Botao ocupado={excluir.isPending} aoClicar={() => {
              aoAvisar(null); excluir.mutate(confirmando.id)
            }}>
              {excluir.isPending ? "Excluindo…" : "Confirmo, excluir"}
            </Botao>
            <Botao aoClicar={() => setConfirmando(null)}>Cancelar</Botao>
          </div>
        </div>
      )}

      <section style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--line2)" }}>
        <h3 style={{
          margin: "0 0 5px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
          textTransform: "uppercase", color: "var(--acao)",
        }}>
          Incluir chave
        </h3>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap", maxWidth: 640 }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={rot}>Texto</label>
            <input value={nova.texto} style={{ ...campo, fontFamily: "var(--fm)" }}
                   placeholder="modulo_acao"
                   onChange={(e) => setNova({ ...nova, texto: e.target.value })} />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={rot}>Grupo</label>
            <input value={nova.grupo} style={campo} placeholder="Projeto"
                   onChange={(e) => setNova({ ...nova, grupo: e.target.value })} />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={rot}>Descrição</label>
            <input value={nova.descricao} style={campo}
                   onChange={(e) => setNova({ ...nova, descricao: e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop: 9 }}>
          <Botao destaque desligado={!nova.texto.trim()} ocupado={criar.isPending}
                 aoClicar={() => { aoAvisar(null); criar.mutate(nova) }}>
            Incluir
          </Botao>
        </div>
        <p style={{ margin: "7px 0 0", fontSize: 10.5, color: "var(--txt3)", maxWidth: "72ch" }}>
          Criar a chave não dá acesso a ninguém: ela precisa ser concedida na aba Operadores, e o
          código precisa perguntar por ela.
        </p>
      </section>
    </div>
  )
}

// ---------------------------------------------------------------- peças

function Botao({ children, aoClicar, destaque, desligado, ocupado }: {
  children: React.ReactNode
  aoClicar: () => void
  destaque?: boolean
  desligado?: boolean
  ocupado?: boolean
}) {
  const inativo = desligado || ocupado
  return (
    <button
      onClick={aoClicar}
      disabled={inativo}
      style={{
        border: "1px solid " + (destaque && !inativo ? "var(--acao)" : "var(--line)"),
        borderRadius: 6, padding: "4px 11px", fontSize: 12,
        cursor: inativo ? "default" : "pointer",
        background: destaque && !inativo ? "var(--acao)" : "var(--surface)",
        color: destaque && !inativo ? "#fff" : inativo ? "var(--txt3)" : "var(--txt)",
      }}
    >
      {children}
    </button>
  )
}

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return "Não foi possível concluir."
}

const campo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
const rot: React.CSSProperties = {
  display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3,
}

/**
 * Criar e alterar operador.
 *
 * **A senha só aparece ao criar.** Alterar não a recebe: editar o telefone de
 * alguém não pode, por um campo vazio no formulário, apagar a senha dessa
 * pessoa. Trocar senha é botão próprio.
 *
 * **Nível 0 é o que desativa**, e não a coluna `ativo` — as duas divergem na
 * base, e é o nível que o login exige.
 */
function Cadastro({ inicial, editando, aoAvisar, aoFechar }: {
  inicial: Rascunho
  editando: Operador | null
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const qc = useQueryClient()
  const [f, setF] = useState<Rascunho>(inicial)
  const [erro, setErro] = useState<string | null>(null)
  const [novaSenha, setNovaSenha] = useState("")

  const muda = <K extends keyof Rascunho>(k: K, v: Rascunho[K]) =>
    setF((a) => ({ ...a, [k]: v }))

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["acesso-operadores"] })
  }

  const gravar = useMutation({
    mutationFn: async () => {
      const corpo = {
        nome: f.nome, email: f.email, nivel: f.nivel,
        telefone: f.telefone || null, ramal: f.ramal || null, sexo: f.sexo || null,
      }
      if (editando) {
        await api.put(`${BASE}/operadores/${editando.id}`, corpo, foraDoProjeto())
      } else {
        await api.post(`${BASE}/operadores`, { ...corpo, senha: f.senha }, foraDoProjeto())
      }
    },
    onSuccess: () => {
      recarregar()
      aoAvisar(`${editando ? "Alterado" : "Criado"}: ${f.nome}.`)
      aoFechar()
    },
    onError: (e) => setErro(mensagem(e)),
  })

  const trocarSenha = useMutation({
    mutationFn: async () => {
      await api.put(`${BASE}/operadores/${editando!.id}/senha`,
        { senha: novaSenha }, foraDoProjeto())
    },
    onSuccess: () => {
      setNovaSenha("")
      aoAvisar(`Senha de ${f.nome} trocada.`)
    },
    onError: (e) => setErro(mensagem(e)),
  })

  const podeGravar = f.nome.trim() && f.email.includes("@") &&
    (editando != null || f.senha.trim().length > 0)

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 16, maxWidth: "70ch" }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600 }}>
        {editando ? `Alterar ${editando.nome}` : "Novo operador"}
      </h3>
      <p style={{ margin: "0 0 14px", fontSize: 11.5, color: "var(--txt3)", lineHeight: 1.6 }}>
        Dado global — a trava recusa gravação em desenvolvimento, e a API aponta para produção.
      </p>

      <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
        <label style={{ flex: 1, minWidth: 190 }}>
          <span style={rotulo}>Nome</span>
          <input value={f.nome} onChange={(e) => muda("nome", e.target.value)} style={campo} />
        </label>
        <label style={{ flex: 1, minWidth: 190 }}>
          <span style={rotulo}>E-mail</span>
          <input value={f.email} onChange={(e) => muda("email", e.target.value)} style={campo} />
        </label>
      </div>

      <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 9 }}>
        <label style={{ flex: "0 0 190px" }}>
          <span style={rotulo}>Nível</span>
          <select value={f.nivel} onChange={(e) => muda("nivel", Number(e.target.value))} style={campo}>
            <option value={0}>0 — Desativado</option>
            <option value={1}>1 — Vendedor</option>
            <option value={2}>2 — Cobrança</option>
            <option value={3}>3 — Administrativo</option>
            <option value={4}>4 — Administrativo e admin. de oportunidades</option>
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 140 }}>
          <span style={rotulo}>Telefone</span>
          <input value={f.telefone} onChange={(e) => muda("telefone", e.target.value)} style={campo} />
        </label>
        <label style={{ flex: "0 0 90px" }}>
          <span style={rotulo}>Ramal</span>
          <input value={f.ramal} onChange={(e) => muda("ramal", e.target.value)} style={campo} />
        </label>
      </div>

      {f.nivel === 0 && (
        <p style={{
          margin: "10px 0 0", padding: "7px 10px", borderRadius: 5, fontSize: 11.5,
          lineHeight: 1.5, background: "var(--aviso-soft, #fdf1d8)",
          color: "var(--aviso-on-soft, #7a4b00)",
        }}>
          Nível 0 <b>desativa</b>: a pessoa deixa de entrar, no Sistema e no PainelAdm. É assim
          que se tira alguém — excluir não existe, para a autoria dos pinouts e diagramas antigos
          continuar fazendo sentido.
        </p>
      )}

      {!editando && (
        <label style={{ display: "block", marginTop: 9 }}>
          <span style={rotulo}>Senha inicial</span>
          <input value={f.senha} onChange={(e) => muda("senha", e.target.value)} style={campo} />
          <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
            Até 36 caracteres — é o tamanho da coluna. Vai para o banco em texto plano, porque o
            login do PainelAdm compara direto no SQL.
          </span>
        </label>
      )}

      {erro && (
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--erro)" }}>{erro}</p>
      )}

      <div style={{ display: "flex", gap: 9, marginTop: 14 }}>
        <button
          onClick={() => { setErro(null); gravar.mutate() }}
          disabled={!podeGravar || gravar.isPending}
          style={{ ...botao, opacity: podeGravar && !gravar.isPending ? 1 : 0.5 }}
        >
          {editando ? "Gravar" : "Criar"}
        </button>
        <button onClick={aoFechar} style={{ ...botao, background: "var(--surface)" }}>
          Cancelar
        </button>
      </div>

      {editando && (
        <div style={{ marginTop: 22, paddingTop: 14, borderTop: "1px solid var(--line2)" }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600 }}>Trocar a senha</h4>
          <div style={{ display: "flex", gap: 9, alignItems: "flex-end" }}>
            <label style={{ flex: 1, maxWidth: 260 }}>
              <span style={rotulo}>Nova senha</span>
              <input value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} style={campo} />
            </label>
            <button
              onClick={() => { setErro(null); trocarSenha.mutate() }}
              disabled={!novaSenha.trim() || trocarSenha.isPending}
              style={{ ...botao, background: "var(--surface)", opacity: novaSenha.trim() ? 1 : 0.5 }}
            >
              Trocar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const rotulo: React.CSSProperties = {
  display: "block", fontSize: 11, color: "var(--txt2)", marginBottom: 3,
}

const botao: React.CSSProperties = {
  padding: "5px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
  border: "1px solid var(--acao)", background: "var(--acao-soft)", color: "var(--txt)",
}
